"use strict";
// New-user tracking window — the ONE place that decides whether a high-volume,
// per-action event is still worth ingesting for a given account.
//
// Why this exists: PostHog bills on ingestion, so the only lever that reduces
// volume is dropping the event client-side before it is sent. Measured over a
// 30-day window, 92% of generation events came from accounts older than 30 days
// while contributing nothing the product questions actually ask — those
// questions ("what do new users do, and where do they get stuck?") are all
// scoped to a user's first days. So the windowed events are kept for new
// accounts and dropped for established ones. See ADR-0036.
//
// Deps-free leaf (ADR-0005 pattern) so every surface can bundle it. Avoids `??`
// and numeric separators on purpose: web's eslint-loader lints the symlinked
// shared/dist with a webpack-4 parser that rejects both, and target ES2022 means
// tsc emits them verbatim rather than downlevelling. Same constraint as
// identity.ts.
Object.defineProperty(exports, "__esModule", { value: true });
exports.SHARED_WINDOWED_EVENTS = exports.NEW_USER_EVENT_WINDOW_DAYS = void 0;
exports.isWithinNewUserWindow = isWithinNewUserWindow;
exports.createNewUserWindowGate = createNewUserWindowGate;
const events_1 = require("./events");
const superProps_1 = require("./superProps");
// Whole days after account creation during which windowed events are still
// captured. 14, NOT 7, deliberately: the New-User Activation funnel uses a
// 14-day conversion window, so a 7-day gate would silently drop day-8..14
// activations and make the funnel understate activation. Any change here must
// be made together with that funnel's funnelWindowInterval.
exports.NEW_USER_EVENT_WINDOW_DAYS = 14;
// The cross-surface events subject to the window. Deliberately excludes
// GENERATION_FAILED: it is a low-volume reliability signal (a few hundred a
// month), not a usage metric, and its value is precisely that it covers the
// whole population. Surfaces add their own surface-specific names to this set
// (web adds board_generation_run, image_downloaded, remix_initiated,
// result_favorited) by referencing their own EVENTS map — never a string
// literal.
exports.SHARED_WINDOWED_EVENTS = [
    events_1.SHARED_EVENTS.GENERATION_RUN,
    events_1.SHARED_EVENTS.GENERATION_SUCCEEDED,
    events_1.SHARED_EVENTS.RESULT_INSERTED,
];
// Is an account still inside the window? Unknown age (null/undefined/NaN)
// ALLOWS the event: age is unknown before identify/register runs, which is
// exactly when the signed-out sign-in-funnel events fire. Failing open there
// keeps those funnels intact, and costs nothing because the windowed events
// can only happen once signed in.
function isWithinNewUserWindow(accountAgeDays) {
    if (accountAgeDays == null || !Number.isFinite(accountAgeDays))
        return true;
    return accountAgeDays <= exports.NEW_USER_EVENT_WINDOW_DAYS;
}
// Build a gate over the given windowed-event names. Each surface creates one
// and wires it into its analytics facade's track/register/reset — call sites
// never change.
//
// Uses a plain array + indexOf rather than a Set: web's eslint-loader lints the
// symlinked shared/dist with an env that has no ES2015 globals declared, so a
// bare `Set` fails the production build with `'Set' is not defined  no-undef`.
// The list is a handful of names, so the lookup cost is irrelevant.
function createNewUserWindowGate(windowedEvents) {
    const windowed = windowedEvents.slice();
    let accountAgeDays = null;
    return {
        observeSuperProps(superProperties) {
            if (!superProperties)
                return;
            if (!Object.prototype.hasOwnProperty.call(superProperties, superProps_1.SUPER_PROPS.ACCOUNT_AGE_DAYS)) {
                return;
            }
            const raw = superProperties[superProps_1.SUPER_PROPS.ACCOUNT_AGE_DAYS];
            accountAgeDays =
                typeof raw === 'number' && Number.isFinite(raw) ? raw : null;
        },
        reset() {
            accountAgeDays = null;
        },
        allows(event) {
            if (windowed.indexOf(event) === -1)
                return true;
            return isWithinNewUserWindow(accountAgeDays);
        },
        currentAccountAgeDays() {
            return accountAgeDays;
        },
    };
}
